/** The one notification system — every decision it makes, in one testable place.
 *
 *  Patient messages, patient arrivals and staff-to-staff messages are three KINDS
 *  arriving through one feed (`GET /api/staff/notifications`), raising one kind of
 *  toast, feeding one badge and one tab title. There is deliberately no second path:
 *  the rule below about what may appear on a lock screen has to be true everywhere, and
 *  a rule written twice is a rule that is false in one of the two places.
 *
 *  ── What may be said where ──────────────────────────────────────────────────────────
 *  IN-APP toasts render inside the authenticated portal, on a screen somebody is already
 *  signed in to. They may name a patient.
 *
 *  OS/BROWSER notifications may not. They appear on lock screens, on shared
 *  workstations, over a projector, in a notification centre that survives the session —
 *  none of which the clinic controls. So `osNotification()` composes its text from the
 *  event's KIND ALONE and never touches a name, a body, a visit type or a time. It does
 *  not read those fields even when the event carries them, which is why the test for it
 *  feeds it an event stuffed with a patient name and a message body.
 *
 *  This mirrors `push_payload()` on the server, which ignores `payload.body` for exactly
 *  the same reason: a notification is a doorbell, not a letter.
 *
 *  ── What a poll may do ──────────────────────────────────────────────────────────────
 *  Nothing. Nothing here marks anything read, and nothing here touches the idle-logoff
 *  timer — see the note at the top of lib/polling.js, which holds that half of the
 *  contract for every poll in the app. The feed endpoint is a pure read on the server
 *  side too, and a backend test reads its source to keep it that way.
 */

/** How often the feed is polled.
 *
 *  Between the two cadences in lib/polling.js on purpose: slower than an open
 *  conversation (10s — somebody is reading it right now), faster than the inbox list
 *  (30s — peripheral vision), because one of the things in this feed is a patient
 *  standing at the front desk.
 */
export const NOTIFY_POLL_MS = 20_000;

/** The kinds this system knows. Anything else is ignored rather than guessed at — a
 *  future server that grows a fourth kind must not produce a toast reading "undefined",
 *  and must not produce an OS notification at all until someone has decided what it may
 *  say. */
export const NOTIFICATION_KINDS = Object.freeze([
  "patient_message", "arrival", "staff_message",
]);

/** The ONLY text that reaches an OS notification. Fixed strings, one per kind.
 *
 *  Not templates. A template is a place a name can be interpolated later by somebody who
 *  did not read this file. */
const OS_TEXT = Object.freeze({
  patient_message: "New patient message",
  arrival: "A patient has arrived",
  staff_message: "New staff message",
});

const FEED_SECTIONS = Object.freeze(["patient_messages", "staff_messages", "arrivals"]);

function sectionEvents(feed, section) {
  const events = feed?.[section]?.events;
  return Array.isArray(events) ? events : [];
}

/** Every event in the feed, oldest first, with unknown kinds dropped. */
export function allEvents(feed) {
  const rows = FEED_SECTIONS.flatMap((s) => sectionEvents(feed, s))
    .filter((e) => e && typeof e.id === "string" && NOTIFICATION_KINDS.includes(e.kind));
  return rows.sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
}

export function allEventIds(feed) {
  return allEvents(feed).map((e) => e.id);
}

/** Events worth interrupting somebody about, given what has already been seen.
 *
 *  `seen` null/undefined means this is the FIRST snapshot of the session, and the answer
 *  is deliberately none: the first poll after signing in would otherwise raise a toast
 *  for every unread message of the last day at once. The first snapshot establishes the
 *  baseline; the second one onwards is news.
 *
 *  Diffing by id on the client — rather than the server remembering a last-polled
 *  marker — is what keeps the feed a pure read. A server-side "notified" flag would be a
 *  write on every poll, which is the thing the whole polling contract forbids.
 */
export function newEvents(feed, seen) {
  if (seen == null) return [];
  const known = seen instanceof Set ? seen : new Set(seen);
  return allEvents(feed).filter((e) => !known.has(e.id));
}

/** The number on the Messages badge and in the tab title.
 *
 *  Patient threads awaiting a staff reply, plus unread staff messages. "Awaiting a
 *  reply" rather than "unread" for the patient half: a message somebody opened and never
 *  answered is still a patient waiting to hear back, and a count that cleared when a tab
 *  happened to be open would say the work was done. The cost is that the badge only
 *  clears when somebody actually replies, which is the honest trade.
 *
 *  ONE number, used in both places. Two slightly different counts on the same screen is
 *  how people come to trust neither.
 */
export function messagesBadgeCount(feed) {
  const awaiting = Number(feed?.patient_messages?.awaiting_reply_threads) || 0;
  const staff = Number(feed?.staff_messages?.unread) || 0;
  return awaiting + staff;
}

/** Unread patient threads — what the badge would say if it were unread-based. Shown on
 *  the Messages card beside the awaiting-reply count, because they are different work. */
export function unreadThreadCount(feed) {
  return Number(feed?.patient_messages?.unread_threads) || 0;
}

/** "(2) RelationshipOS", or the bare title at zero.
 *
 *  A "(0)" prefix is noise that never goes away, and a tab that always looks like it
 *  wants something is a tab nobody looks at.
 */
export function tabTitle(count, base = "RelationshipOS") {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return base;
  return `(${n}) ${base}`;
}

/** The in-app toast for one event: what it says, and where clicking it goes.
 *
 *  This one may name a patient — it is rendered inside the portal by somebody who is
 *  signed in. It still carries no message BODY: the feed does not send one, so there is
 *  nothing here to leak even into a screenshot.
 */
export function inAppToast(event) {
  if (!event || !NOTIFICATION_KINDS.includes(event.kind)) return null;
  const who = event.patient_name || "A patient";
  switch (event.kind) {
    case "patient_message":
      return {
        text: `New message from ${who}`,
        href: event.patient_id ? `/messages?patient=${event.patient_id}` : "/messages",
      };
    case "arrival": {
      const when = [event.time, event.visit_type].filter(Boolean).join(" ");
      return {
        text: when ? `${who} has arrived — ${when}` : `${who} has arrived`,
        href: "/schedule",
      };
    }
    case "staff_message":
      return {
        text: `New message from ${event.sender_name || "a colleague"}`,
        href: event.conversation_id
          ? `/messages?view=team&conversation=${encodeURIComponent(event.conversation_id)}`
          : "/messages?view=team",
      };
    default:
      return null;
  }
}

/** The OS/browser notification for one event, or null if there must not be one.
 *
 *  Reads `event.kind` and NOTHING ELSE off the event. The clinic name is the only
 *  variable part, and it is the clinic's own name — not a person's.
 */
export function osNotification(event, clinicName) {
  const title = OS_TEXT[event?.kind];
  if (!title) return null;
  const body = typeof clinicName === "string" ? clinicName.trim() : "";
  return { title, body };
}

/** May an OS notification be shown at all?
 *
 *  Four independent gates, and the order does not matter because every one of them is a
 *  hard no:
 *
 *   - `authenticated`: no notifications once the session is gone. An idle auto-logoff
 *     leaves the tab open, and a tab that keeps announcing patient activity after the
 *     session it belonged to has expired is the unattended-screen failure that automatic
 *     logoff exists to prevent, arriving by a different door.
 *   - `locked`: same argument for a session that is present but locked.
 *   - `optedIn`: the per-user setting, off by default, stored server-side.
 *   - `permission`: the browser's own answer. "default" means nobody has been asked, and
 *     an un-asked browser must not be treated as a yes.
 */
export function canDeliverOsNotification({ authenticated, locked, optedIn, permission } = {}) {
  if (!authenticated) return false;
  if (locked) return false;
  if (!optedIn) return false;
  return permission === "granted";
}

/** May an IN-APP toast be shown? Same session gates, no opt-in and no permission: a
 *  toast inside the portal is part of the portal, and it is not going anywhere the
 *  signed-in person cannot already see. */
export function canDeliverInAppToast({ authenticated, locked } = {}) {
  return Boolean(authenticated) && !locked;
}
